import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { getMoodBoardsPaginated } from '~/modules/build-hub/api/get-moodboards-paginated.server';
import type { GetMoodBoardsPaginatedResponse } from '~/modules/build-hub/api/get-moodboards-paginated.server';
import { isUserLoggedIn } from '~/modules/user/hook/use-user';
import { hasFeature } from '~/modules/root/hook/use-feature';

export const MOODBOARDS_PAGINATED_API = '/remix-api/moodboards/list-paginated';

const EMPTY_MOODBOARDS_RESPONSE: GetMoodBoardsPaginatedResponse = {
    boards: [],
    pageInfo: {
        __typename: 'APPageInfo',
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    },
};

/**
 * API endpoint to get paginated moodboards with optional search and deduplication.
 * Supports infinite scroll via offset/limit parameters.
 *
 * Query params:
 * - limit: number (default 10, max 50)
 * - offset: number (default 0)
 * - search: string (optional - searches title and BuildHub title)
 * - excludeIds: comma-separated IDs to exclude (optional - for deduplication)
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { user } = context;

    if (!isUserLoggedIn(user)) {
        return routerData(EMPTY_MOODBOARDS_RESPONSE, { status: 401 });
    }

    const url = new URL(request.url);
    const limitParam = url.searchParams.get('limit');
    const offsetParam = url.searchParams.get('offset');
    const searchParam = url.searchParams.get('search');
    const excludeIdsParam = url.searchParams.get('excludeIds');
    const buildHubIdParam = url.searchParams.get('buildHubId');
    const ownerOnlyParam = url.searchParams.get('ownerOnly');
    const nonProjectOnlyParam = url.searchParams.get('nonProjectOnly');
    const canEditParam = url.searchParams.get('canEdit');
    const requestIdParam = url.searchParams.get('requestId');

    // Parse and validate limit (default 10, max 50)
    const limit = limitParam ? Math.min(parseInt(limitParam, 10), 50) : 10;

    // Parse offset (default 0)
    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;

    // Parse excludeIds if provided
    const excludeIds = excludeIdsParam
        ? excludeIdsParam
              .split(',')
              .map((id) => parseInt(id.trim(), 10))
              .filter((id) => !isNaN(id))
        : undefined;
    const buildHubId = buildHubIdParam
        ? parseInt(buildHubIdParam, 10)
        : undefined;
    const ownerOnly = ownerOnlyParam === 'true';
    const nonProjectOnly = nonProjectOnlyParam === 'true';
    const canEdit = canEditParam === 'false' ? false : true;

    const buildHubEnabled = hasFeature(context.featureFlags, 'buildhub');

    try {
        const data = await getMoodBoardsPaginated(
            {
                limit,
                offset,
                search: searchParam || undefined,
                excludeIds,
                buildHubId,
                ownerOnly,
                nonProjectOnly,
                canEdit,
                ...(!buildHubEnabled && {
                    includeFollowed: true,
                    nonProjectOnly: true,
                }),
            },
            context,
        );

        return routerData(
            {
                ...data,
                ...(requestIdParam ? { requestId: requestIdParam } : {}),
            },
            {
                headers: {
                    'Cache-Control': 'private, no-cache',
                },
            },
        );
    } catch (error) {
        context.logger.error(`Error in ${MOODBOARDS_PAGINATED_API}`, {
            error,
        });
        return routerData(EMPTY_MOODBOARDS_RESPONSE, { status: 500 });
    }
};
